/* concept-pdp-tabs.js
   Ivycoast Concept PDP — two responsibilities:
   1. Hides the product description block (it becomes the "Details" tab)
   2. Transforms vertical accordions + description into a horizontal tab bar
      — "Details" tab is prepended from .product-short-description
      — remaining tabs come from .thb-tab accordions
   Loaded by main-product.liquid (deferred, no module required).
*/
(function () {
  'use strict';

  /* ────────────────────────────────────────────────────────────────
     Shopify rich-text AST renderer.
     When a collapsible_tab block's "content" setting is connected
     to a rich_text metafield via dynamic source, Shopify outputs
     the raw AST as a Ruby-style hash string (using => instead of :).
     metafield_tag can't handle it in this context, so we parse and
     render it in JS instead.
  ──────────────────────────────────────────────────────────────── */
  function renderRichTextNode(node) {
    if (!node || !node.type) return '';
    var children = (node.children || []).map(renderRichTextNode).join('');
    switch (node.type) {
      case 'root':      return children;
      case 'paragraph':
        if (!children.trim()) return ''; /* skip empty paragraphs */
        return '<p>' + children + '</p>';
      case 'list':
        var tag = node.listType === 'ordered' ? 'ol' : 'ul';
        return '<' + tag + '>' + children + '</' + tag + '>';
      case 'list-item': return '<li>' + children + '</li>';
      case 'heading':   return '<h' + (node.level || 2) + '>' + children + '</h' + (node.level || 2) + '>';
      case 'link':
        var href = node.url || '#';
        var rel  = node.newTab ? ' target="_blank" rel="noopener"' : '';
        return '<a href="' + href + '"' + rel + '>' + children + '</a>';
      case 'text':
        var txt = (node.value || '')
          .replace(/\u2028/g, '')        /* strip Unicode Line Separator */
          .replace(/&/g, '&amp;')        /* escape HTML before inserting tags */
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br>');       /* newline → <br> after escaping */
        if (node.bold)   txt = '<strong>' + txt + '</strong>';
        if (node.italic) txt = '<em>'     + txt + '</em>';
        if (node.code)   txt = '<code>'   + txt + '</code>';
        return txt;
      default: return children;
    }
  }

  function maybeRenderRichTextPanel(panel) {
    var raw = (panel.textContent || panel.innerText || '').trim();
    /* Shopify outputs Ruby hashes with => — detect by leading { and => */
    if (raw.charAt(0) !== '{' || raw.indexOf('=>') === -1) return;
    try {
      /* Convert Ruby hash syntax to JSON: => → : and nil → null */
      var json = raw.replace(/=>/g, ':').replace(/\bnil\b/g, 'null');
      var ast  = JSON.parse(json);
      var html = '<div class="ic-rich-text">' + renderRichTextNode(ast) + '</div>';
      panel.innerHTML = html;
    } catch (e) {
      /* Leave untouched if parse fails */
    }
  }

  /* ────────────────────────────────────────────────────────────────
     Hide .product-short-description — its content is injected as
     the first "Details" tab by initTabs() instead.
  ──────────────────────────────────────────────────────────────── */
  function repositionDescription() {
    var infoPanel = document.querySelector(
      '.thb-product-detail .product-information'
    );
    if (!infoPanel) return;

    var descEl = infoPanel.querySelector('.product-short-description');
    if (descEl) {
      descEl.style.display = 'none';
    }
  }

  function initTabs() {
    var infoPanel = document.querySelector(
      '.thb-product-detail .product-information'
    );
    if (!infoPanel) return;

    var accordions = Array.prototype.slice.call(
      infoPanel.querySelectorAll('.thb-tab')
    );

    /* ── 1. Build tab data — Details first, then accordions ── */
    var tabData = [];

    /* Prepend description as "Details" tab if it has content */
    var descEl = infoPanel.querySelector('.product-short-description');
    if (descEl && descEl.textContent.trim()) {
      tabData.push({ label: 'Details', contentEl: descEl });
    }

    /* Harvest label + content refs from each accordion */
    accordions.forEach(function (acc) {
      var labelEl = acc.querySelector('.tab-label');
      var contentEl = acc.querySelector('.tab-content');

      var labelText = '';
      if (labelEl) {
        var clone = labelEl.cloneNode(true);
        var arrow = clone.querySelector('.thb-accordion-arrow');
        if (arrow) arrow.parentNode.removeChild(arrow);
        labelText = (clone.textContent || clone.innerText || '').trim();
      }

      tabData.push({ label: labelText, contentEl: contentEl });
    });

    if (tabData.length === 0) return;

    /* ── 2. Build tab bar ── */
    var bar = document.createElement('div');
    bar.className = 'ic-tab-bar';
    bar.setAttribute('role', 'tablist');

    /* ── 3. Build content wrapper ── */
    var wrap = document.createElement('div');
    wrap.className = 'ic-tab-content-wrap';

    tabData.forEach(function (td, i) {
      /* button */
      var btn = document.createElement('button');
      btn.className = 'ic-tab-btn' + (i === 0 ? ' is-active' : '');
      btn.type = 'button';

      /* Split "JP / EN" label into locale-aware spans */
      var labelParts = td.label.split(' / ');
      if (labelParts.length === 2) {
        var jpSpan = document.createElement('span');
        jpSpan.className = 'ic-tab-jp';
        jpSpan.textContent = labelParts[0].trim();
        var enSpan = document.createElement('span');
        enSpan.className = 'ic-tab-en';
        enSpan.textContent = labelParts[1].trim();
        btn.appendChild(jpSpan);
        btn.appendChild(enSpan);
      } else {
        btn.textContent = td.label;
      }

      btn.setAttribute('role', 'tab');
      btn.setAttribute('data-tab-index', String(i));
      btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      bar.appendChild(btn);

      /* panel — clone server-rendered DOM nodes from the hidden accordion */
      var panel = document.createElement('div');
      panel.className = 'ic-tab-panel' + (i === 0 ? ' is-active' : '');
      panel.setAttribute('role', 'tabpanel');

      if (td.contentEl) {
        var nodes = Array.prototype.slice.call(td.contentEl.childNodes);
        nodes.forEach(function (node) {
          panel.appendChild(node.cloneNode(true));
        });
      }

      /* Convert raw Shopify rich-text AST to HTML if needed */
      maybeRenderRichTextPanel(panel);

      wrap.appendChild(panel);
    });

    /* ── 4. Click delegation ── */
    bar.addEventListener('click', function (e) {
      var target = e.target;
      while (target && !target.classList.contains('ic-tab-btn')) {
        target = target.parentElement;
      }
      if (!target) return;

      var idx = parseInt(target.getAttribute('data-tab-index'), 10);
      if (isNaN(idx)) return;

      var allBtns = bar.querySelectorAll('.ic-tab-btn');
      var allPanels = wrap.querySelectorAll('.ic-tab-panel');

      for (var j = 0; j < allBtns.length; j++) {
        allBtns[j].classList.remove('is-active');
        allBtns[j].setAttribute('aria-selected', 'false');
        allPanels[j].classList.remove('is-active');
      }

      target.classList.add('is-active');
      target.setAttribute('aria-selected', 'true');
      allPanels[idx].classList.add('is-active');
    });

    /* ── 5. Insert UI, hide originals ── */
    /* Find an anchor: first accordion, or if none, the desc element itself */
    var anchor = accordions.length > 0 ? accordions[0] : descEl;
    if (!anchor) return;

    anchor.parentNode.insertBefore(bar, anchor);
    anchor.parentNode.insertBefore(wrap, anchor);

    /* Hide all source elements */
    accordions.forEach(function (acc) {
      acc.style.display = 'none';
    });
    /* descEl is already hidden by repositionDescription() */
  }

  /* ────────────────────────────────────────────────────────────────
     Wrap price + judge.me rating into a flex row (.ic-price-rating-row).
     Figma: price LEFT (shrink-0), rating RIGHT (flex-1), gap 16px.
     Must run after judge.me has rendered its badge — use a short
     setTimeout so app blocks have time to inject their markup.
  ──────────────────────────────────────────────────────────────── */
  function buildPriceRatingRow() {
    setTimeout(function () {
      var summary = document.querySelector(
        '.thb-product-detail .product-information .summary'
      );
      if (!summary) return;

      var priceEl = summary.querySelector('.product-price-container');
      /* Judge.me preview badge — common class names across versions */
      var ratingEl = summary.querySelector(
        '.jdgm-preview-badge, [class*="jdgm-prev-badge"], [class*="jdgm-preview"]'
      );

      /* Nothing to wrap if price missing, or both already inside our row */
      if (!priceEl || priceEl.closest('.ic-price-rating-row')) return;

      /* Walk up from ratingEl to find its direct-child-of-summary ancestor */
      var ratingChild = ratingEl;
      while (ratingChild && ratingChild.parentElement !== summary) {
        ratingChild = ratingChild.parentElement;
      }

      /* Build the row */
      var row = document.createElement('div');
      row.className = 'ic-price-rating-row';

      /* Insert the row where price currently lives */
      summary.insertBefore(row, priceEl);

      /* Price LEFT (per Figma), then rating RIGHT */
      row.appendChild(priceEl);
      if (ratingChild) row.appendChild(ratingChild);
    }, 50); /* 50ms lets judge.me inject its badge before we look for it */
  }

  /* ────────────────────────────────────────────────────────────────
     Build custom gallery pagination dots.
     Flickity is initialised with pageDots:false, so we create our own
     .ic-gallery-dots strip and keep it in sync via MutationObserver
     (watching for the .is-selected class change on .product-images__slide).
  ──────────────────────────────────────────────────────────────── */
  function initGalleryDots() {
    var container = document.querySelector(
      '.thb-product-detail.thb-product-style1 .product-gallery-container'
    );
    if (!container) return;

    var slides = container.querySelectorAll('.product-images__slide');
    if (slides.length < 2) return; /* no dots needed for 0-1 slides */

    /* ── Build dots strip ── */
    var strip = document.createElement('div');
    strip.className = 'ic-gallery-dots';

    var dotEls = [];
    for (var i = 0; i < slides.length; i++) {
      var dot = document.createElement('span');
      dot.className = 'ic-gallery-dot' + (i === 0 ? ' is-active' : '');
      strip.appendChild(dot);
      dotEls.push(dot);
    }

    container.appendChild(strip);

    /* ── Sync dots when Flickity changes active slide ── */
    function syncDots() {
      for (var j = 0; j < slides.length; j++) {
        if (slides[j].classList.contains('is-selected')) {
          dotEls[j].classList.add('is-active');
        } else {
          dotEls[j].classList.remove('is-active');
        }
      }
    }

    /* MutationObserver watches class changes on each slide */
    var observer = new MutationObserver(syncDots);
    for (var k = 0; k < slides.length; k++) {
      observer.observe(slides[k], { attributes: true, attributeFilter: ['class'] });
    }
  }

  /* ────────────────────────────────────────────────────────────────
     Build a custom quantity pill that bypasses app.css entirely.
     app.css uses position:absolute on .plus/.minus — unwinnable via
     CSS overrides. Instead we:
       1. Hide the original quantity-selector (kept for Shopify JS)
       2. Insert a clean .ic-qty-pill div with our own <button> elements
       3. Wire our buttons to click() the hidden inputs so Shopify's
          cart/variant logic fires as normal
  ──────────────────────────────────────────────────────────────── */
  function buildQtyPill() {
    setTimeout(function () {
      var qs = document.querySelector(
        '.thb-product-detail .product-information quantity-selector'
      );
      if (!qs || qs.classList.contains('ic-qty-replaced')) return;

      var qtyInput   = qs.querySelector('input.qty');
      var plusInput  = qs.querySelector('input.plus');
      var minusInput = qs.querySelector('input.minus');
      if (!qtyInput) return;

      /* ── 1. Hide the original quantity-selector ── */
      qs.classList.add('ic-qty-replaced');

      /* ── 2. Build the replacement pill ── */
      var pill = document.createElement('div');
      pill.className = 'ic-qty-pill';

      var minusBtn = document.createElement('button');
      minusBtn.type = 'button';
      minusBtn.className = 'ic-qty-btn ic-qty-minus';
      minusBtn.setAttribute('aria-label', 'Decrease quantity');
      minusBtn.textContent = '-';  /* Figma shows hyphen-minus */

      var qtyDisplay = document.createElement('span');
      qtyDisplay.className = 'ic-qty-value';
      qtyDisplay.textContent = qtyInput.value || '1';

      var plusBtn = document.createElement('button');
      plusBtn.type = 'button';
      plusBtn.className = 'ic-qty-btn ic-qty-plus';
      plusBtn.setAttribute('aria-label', 'Increase quantity');
      plusBtn.textContent = '+';

      pill.appendChild(minusBtn);
      pill.appendChild(qtyDisplay);
      pill.appendChild(plusBtn);

      /* ── 3. Sync display with the hidden input ── */
      function syncDisplay() {
        qtyDisplay.textContent = qtyInput.value;
        var cur = parseInt(qtyInput.value, 10) || 1;
        var min = parseInt(qtyInput.min || qtyInput.dataset.min || '1', 10);
        var max = parseInt(qtyInput.max || qtyInput.dataset.max || '9999', 10);
        minusBtn.disabled = cur <= min;
        plusBtn.disabled  = cur >= max;
      }

      minusBtn.addEventListener('click', function () {
        if (minusInput) {
          minusInput.click();
        } else {
          var cur = parseInt(qtyInput.value, 10) || 1;
          var min = parseInt(qtyInput.min || qtyInput.dataset.min || '1', 10);
          if (cur > min) {
            qtyInput.value = cur - 1;
            qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        setTimeout(syncDisplay, 30);
      });

      plusBtn.addEventListener('click', function () {
        if (plusInput) {
          plusInput.click();
        } else {
          var cur = parseInt(qtyInput.value, 10) || 1;
          var max = parseInt(qtyInput.max || qtyInput.dataset.max || '9999', 10);
          if (cur < max) {
            qtyInput.value = cur + 1;
            qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        setTimeout(syncDisplay, 30);
      });

      /* Also sync if Shopify updates the hidden input directly */
      qtyInput.addEventListener('change', syncDisplay);
      qtyInput.addEventListener('input',  syncDisplay);

      syncDisplay();

      /* ── 4. Insert pill immediately before the hidden quantity-selector ── */
      qs.parentNode.insertBefore(pill, qs);
    }, 0);
  }

  /* ────────────────────────────────────────────────────────────────
     Ensure judge.me summary average number is visible on mobile.
     Judge.me sometimes leaves .jdgm-rev-widg__summary-average empty
     and populates it via JS after load. We wait briefly, then if the
     element has text content, make sure it's not hidden by any inline
     style. If it's empty, hide the element so it doesn't create a gap.
  ──────────────────────────────────────────────────────────────── */
  /* ────────────────────────────────────────────────────────────────
     Build editorial score display for judge.me widget.
     Judge.me's .jdgm-rev-widg__summary-average element is absent or
     contains a locale string we can't rely on. Instead we:
       1. Read the score from the preview badge (always rendered, has data-score)
       2. Build a .ic-review-score element: ★ + number
       3. Prepend it into .jdgm-rev-widg__summary-inner before the count text
  ──────────────────────────────────────────────────────────────── */
  function buildReviewScore() {
    /* Don't run twice */
    if (document.querySelector('.ic-review-score')) return;

    var summaryEl  = document.querySelector('.jdgm-rev-widg__summary');
    var scoreInner = document.querySelector('.jdgm-rev-widg__summary-inner');
    if (!scoreInner || !summaryEl) return;

    /* ── Score: try four sources ── */
    var score = null;

    /* Source 1: preview badge [data-score] */
    var badgeStars = document.querySelector(
      '.jdgm-preview-badge [data-score], [class*="jdgm-prev-badge"] [data-score]'
    );
    if (badgeStars) {
      var ds = parseFloat(badgeStars.getAttribute('data-score'));
      if (!isNaN(ds)) score = ds.toFixed(1);
    }

    /* Source 2: aria-label on the hidden summary-stars (always in DOM) */
    if (!score) {
      var summaryStars = document.querySelector('.jdgm-rev-widg__summary-stars');
      if (summaryStars) {
        var lbl = summaryStars.getAttribute('aria-label') || '';
        /* e.g. "Average rating is 5.00 stars" */
        var m = lbl.match(/(\d+(?:\.\d+)?)/);
        if (m) score = parseFloat(m[1]).toFixed(1);
      }
    }

    /* Source 3: count filled stars */
    if (!score) {
      var filled = document.querySelectorAll(
        '.jdgm-rev-widg__summary-stars .jdgm--on'
      );
      if (filled.length > 0) score = filled.length.toFixed(1);
    }

    /* Source 4: locale string in average element */
    if (!score) {
      var avgEl = document.querySelector('.jdgm-rev-widg__summary-average');
      if (avgEl) {
        var raw = (avgEl.textContent || avgEl.innerText || '').trim();
        var decimals = raw.match(/\d+\.\d+/g);
        if (decimals && decimals.length > 0) {
          score = parseFloat(decimals[decimals.length - 1]).toFixed(1);
        }
      }
    }

    if (!score) return;

    /* ── Build .ic-review-score and inject into scoreInner ── */
    var scoreEl = document.createElement('div');
    scoreEl.className = 'ic-review-score';

    var starSpan = document.createElement('span');
    starSpan.className = 'ic-review-score__star';
    starSpan.textContent = '★';

    var numSpan = document.createElement('span');
    numSpan.className = 'ic-review-score__num';
    numSpan.textContent = score;

    scoreEl.appendChild(starSpan);
    scoreEl.appendChild(numSpan);

    var countEl = scoreInner.querySelector('.jdgm-rev-widg__summary-text');
    if (countEl) {
      scoreInner.insertBefore(scoreEl, countEl);
    } else {
      scoreInner.insertBefore(scoreEl, scoreInner.firstChild);
    }

    /* ── Build write-review button and append to the outer summary row ── */
    var writeBtn = document.createElement('button');
    writeBtn.className = 'ic-write-review-btn';
    writeBtn.type = 'button';
    writeBtn.textContent = 'レビューを書く';

    writeBtn.addEventListener('click', function () {
      /* Try to trigger Judge.me's native write-review button */
      var jdgmTrigger = document.querySelector(
        '.jdgm-write-rev-link, .jdgm-wrev-btn, [class*="write-rev"]'
      );
      if (jdgmTrigger) {
        jdgmTrigger.click();
      }
    });

    summaryEl.appendChild(writeBtn);
  }

  function fixJudgeMeAverage() {
    setTimeout(buildReviewScore, 400);
  }

  function init() {
    repositionDescription();
    buildPriceRatingRow();
    buildQtyPill();
    initGalleryDots();
    initTabs();
    fixJudgeMeAverage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
